/**
 * LogList - shared log entry display component
 *
 * Used by both ErrorOverlay (global) and NodeLogPanel (per-node).
 */

import { useState, useCallback } from 'react';
import { LogLevel, LogEntry } from '@/logging';

export const LEVEL_COLORS: Record<LogLevel, string> = {
  [LogLevel.Error]: '#ef4444',
  [LogLevel.Info]: '#3b82f6',
  [LogLevel.Trace]: '#6b7280'
};

interface LogEntryItemProps {
  entry: LogEntry;
  compact?: boolean;
}

export function LogEntryItem({ entry, compact }: LogEntryItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDetails = !!entry.stack;

  const toggleExpanded = useCallback(() => {
    if (hasDetails) setIsExpanded(prev => !prev);
  }, [hasDetails]);

  if (compact) {
    return (
      <div
        style={{
          ...styles.itemCompact,
          borderLeft: `2px solid ${LEVEL_COLORS[entry.level]}`
        }}
      >
        <span style={{ color: LEVEL_COLORS[entry.level], fontSize: 9, marginRight: 6 }}>
          {entry.level.charAt(0).toUpperCase()}
        </span>
        <span style={styles.messageCompact}>{entry.message}</span>
      </div>
    );
  }

  return (
    <div
      onClick={toggleExpanded}
      style={{
        ...styles.item,
        borderLeft: `3px solid ${LEVEL_COLORS[entry.level]}`,
        cursor: hasDetails ? 'pointer' : 'default'
      }}
    >
      <div style={styles.itemHeader}>
        {hasDetails && (
          <span style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
        )}
        <span style={styles.source}>{entry.source}</span>
        {entry.context && (
          <span style={styles.context}>{entry.context}</span>
        )}
        <span style={{ ...styles.level, color: LEVEL_COLORS[entry.level] }}>
          {entry.level}
        </span>
      </div>
      <span style={styles.message}>{entry.message}</span>
      {isExpanded && entry.stack && (
        <pre style={styles.stack}>{entry.stack}</pre>
      )}
    </div>
  );
}

interface LogListProps {
  entries: LogEntry[];
  maxItems?: number;
  compact?: boolean;
  emptyMessage?: string;
}

export function LogList({ entries, maxItems = 15, compact, emptyMessage }: LogListProps) {
  const displayEntries = entries.slice(-maxItems).reverse();

  if (displayEntries.length === 0 && emptyMessage) {
    return <div style={styles.empty}>{emptyMessage}</div>;
  }

  return (
    <div style={compact ? styles.listCompact : styles.list}>
      {displayEntries.map((entry, i) => (
        <LogEntryItem
          key={`${entry.timestamp}-${i}`}
          entry={entry}
          compact={compact}
        />
      ))}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  list: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: 8
  },
  listCompact: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: 6
  },
  item: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    padding: '8px 10px',
    marginBottom: 6,
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 4,
    fontSize: 12
  },
  itemCompact: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '3px 5px',
    marginBottom: 3,
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 3,
    fontSize: 9
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  source: {
    fontWeight: 'bold',
    fontSize: 10,
    textTransform: 'uppercase' as const,
    color: '#9ca3af'
  },
  context: {
    color: '#6b7280',
    fontFamily: 'monospace',
    fontSize: 11
  },
  level: {
    marginLeft: 'auto',
    fontSize: 10,
    textTransform: 'uppercase' as const
  },
  message: {
    color: '#fef2f2',
    lineHeight: 1.4
  },
  messageCompact: {
    color: '#e5e7eb',
    lineHeight: 1.3,
    wordBreak: 'break-word' as const,
    flex: 1
  },
  expandIcon: {
    fontSize: 10,
    marginRight: 6,
    color: '#9ca3af'
  },
  stack: {
    marginTop: 8,
    padding: 8,
    background: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 4,
    fontSize: 10,
    fontFamily: 'monospace',
    color: '#9ca3af',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    overflow: 'auto',
    maxHeight: 150
  },
  empty: {
    color: '#6b7280',
    fontSize: 11,
    textAlign: 'center' as const,
    padding: 12
  }
};
